

/** This will be replaced by the types information JSON */
const __needleTypesJson: unknown = /* @_TYPES_START */[]; /* @_TYPES_END */

let typesInfo: Map<string, NeedleTypeEntry> | null = null;

export type NeedleTypeMemberEntry = {
    name: string,
    kind?: "property" | ({} & string),
    type?: string,
    kindString?: "Property" | ({} & string),
    propertyType?: string,
    /** typed JSON default from the field initializer (new-format emits) */
    defaultValue?: unknown,
    flags?: {
        /** the member is a wire field (@serializable) — the editable surface */
        isSerializable?: boolean,
        isOptional?: boolean,
        isReadonly?: boolean,
        isAccessor?: boolean,
    },
    /** absent on new-format members without a doc comment */
    comment?: {
        kind: "text",
        shortText?: string,
        text?: string,
    }
};

export type NeedleTypeEntry = {
    file: string,
    name: string,
    /** 6.x shape */
    kind?: string,
    groups?: string[],
    categories?: string[],
    /** legacy (pre-6) shape */
    group?: string,
    kindString?: string,
    category?: string | string[],
    flags?: {
        /** an engine COMPONENT (addable to objects) — new-format emits set this */
        isComponent?: boolean,
        /** a serializable DATA class (model referenced by component members) */
        isDataClass?: boolean,
        isAbstract?: boolean,
        /** an enum entry (children are its enum-members) */
        isEnum?: boolean,
        /** bitmask enum → multi-select UI */
        isFlags?: boolean,
    },
    /** absent on new-format entries without a doc comment */
    comment?: {
        kind: "text",
        shortText?: string,
        text?: string,
    },
    /** name of the direct base class (new-format emits), e.g. "Behaviour" */
    inheritedFrom?: string,
    /** absent on new-format entries without members (e.g. marker types) */
    children?: Array<NeedleTypeMemberEntry>,

    __description_text?: string;
}

function parseTypes(): void {
    if (typesInfo !== null) {
        return;
    }
    typesInfo = new Map<string, NeedleTypeEntry>();
    try {
        const obj = typeof __needleTypesJson !== "string" ? __needleTypesJson : JSON.parse(__needleTypesJson) as Array<NeedleTypeEntry>;
        if (Array.isArray(obj)) {
            typesInfo = new Map<string, NeedleTypeEntry>();
            for (const entry of obj) {
                typesInfo.set(entry.name, entry);
            }
        }
        else if (obj) {
            console.debug("Unknown types format");
        }

    } catch (e) {
        // ignore
    }
}

export namespace NeedleMeta {

    export function getNeedleTypeMeta(name: string) {
        parseTypes();
        return typesInfo?.get(name);
    }

    export function getPropertyDescription(componentName: string, propertyName: string) {
        const propInfo = findMemberMeta(componentName, propertyName);
        if (!propInfo) return null;
        return propInfo.comment?.text || propInfo.comment?.shortText || null;
    }

    /** the entry's categories (6.x `categories`; legacy `category` tolerated) */
    export function getTypeCategories(name: string): string[] {
        const typeInfo = getNeedleTypeMeta(name);
        if (!typeInfo) return [];
        if (Array.isArray(typeInfo.categories)) return typeInfo.categories;
        if (Array.isArray(typeInfo.category)) return typeInfo.category;
        if (typeof typeInfo.category === "string") return [typeInfo.category];
        return [];
    }

    /**
     * Is the entry an engine COMPONENT (addable via the Add menu)?
     * New-format entries carry `flags.isComponent`; legacy files listed ONLY
     * components, so an entry without flags counts as one — filtering must not
     * empty the Add menu on an old json.
     */
    export function isComponentType(name: string): boolean {
        const typeInfo = getNeedleTypeMeta(name);
        if (!typeInfo) return false;
        if (typeInfo.flags && (typeInfo.flags.isComponent !== undefined || typeInfo.flags.isDataClass !== undefined)) {
            return typeInfo.flags.isComponent === true;
        }
        return typeInfo.kind === "class" || typeInfo.kindString === "Class" || (!typeInfo.kind && !typeInfo.kindString);
    }

    /** a serializable DATA class (model type referenced by component members) */
    export function isDataClassType(name: string): boolean {
        return getNeedleTypeMeta(name)?.flags?.isDataClass === true;
    }

    /** the SERIALIZABLE members of a data class — the editable wire surface */
    export function getDataClassMembers(name: string): NeedleTypeMemberEntry[] {
        const typeInfo = getNeedleTypeMeta(name);
        if (!typeInfo || typeInfo.flags?.isDataClass !== true) return [];
        return typeInfo.children?.filter(c => c.flags?.isSerializable === true && c.flags?.isReadonly !== true) ?? [];
    }

    /**
     * Resolve a component member declared as an ARRAY of a data class:
     * `trackedImages: "WebXRImageTrackingModel[]"` → the element entry name.
     * Null when the member is not an array of a known data class.
     */
    export function getArrayElementDataClass(componentName: string, propertyName: string): string | null {
        const typeInfo = getNeedleTypeMeta(componentName);
        const member = typeInfo?.children?.find(c => c.name === propertyName);
        const type = member?.type ?? member?.propertyType;
        if (!type || !type.endsWith("[]")) return null;
        const element = type.substring(0, type.length - 2).trim();
        return isDataClassType(element) ? element : null;
    }

    /**
     * A NEW element for a data-class array, constructed from the emitted
     * `defaultValue`s (typed JSON). Fields without a default: optional ones are
     * omitted; required ones get the type-zero value so the engine never reads
     * `undefined` where a number/boolean/string is declared.
     */
    export function createDataClassValue(name: string): Record<string, unknown> | null {
        const members = getDataClassMembers(name);
        if (members.length === 0) return null;
        const value: Record<string, unknown> = {};
        for (const member of members) {
            if (member.defaultValue !== undefined) {
                value[member.name] = member.defaultValue;
                continue;
            }
            if (member.flags?.isOptional === true) continue;
            switch (member.type ?? member.propertyType) {
                case "number": value[member.name] = 0; break;
                case "boolean": value[member.name] = false; break;
                case "string": value[member.name] = ""; break;
                default: break; // object-ish without default: leave unset
            }
        }
        return value;
    }

    /**
     * Enum options for a component member whose declared type is an enum entry:
     * the enum-members' NAMES with their values ("XZPlane" → 0), plus whether
     * the enum is a bitmask (`isFlags` → multi-select UI). Null when the member
     * or its type is not a known enum — the row renders as before then.
     */
    /** Find a member on the type OR any of its base classes (the emit lists
     *  only own members — inherited ones live on the `inheritedFrom` chain). */
    function findMemberMeta(typeName: string, propertyName: string): NeedleTypeMemberEntry | undefined {
        const seen = new Set<string>();
        for (let name: string | undefined = typeName; name && !seen.has(name); ) {
            seen.add(name);
            const entry = getNeedleTypeMeta(name);
            const member = entry?.children?.find(c => c.name === propertyName);
            if (member) return member;
            name = entry?.inheritedFrom;
        }
        return undefined;
    }

    export function getEnumOptions(componentName: string, propertyName: string): { options: Array<{ label: string; value: number | string }>; multiple: boolean } | null {
        const member = findMemberMeta(componentName, propertyName);
        const typeName = member?.type ?? member?.propertyType;
        if (!typeName) return null;
        const entry = getNeedleTypeMeta(typeName);
        if (!entry) return null;
        const isEnum = entry.kind === "enum" || entry.kindString === "Enumeration" || entry.flags?.isEnum === true;
        if (!isEnum || !entry.children?.length) return null;
        const options: Array<{ label: string; value: number | string }> = [];
        for (const child of entry.children) {
            if (child.kind !== "enum-member" && child.kindString !== "Enumeration member") continue;
            // the member VALUE rides in `type` as its literal ("0", or a quoted string)
            const raw = child.type ?? child.propertyType;
            const numeric = raw !== undefined && raw !== "" ? Number(raw) : NaN;
            const value = !isNaN(numeric) ? numeric : (raw ?? child.name).replace(/^"|"$/g, "");
            options.push({ label: child.name, value });
        }
        if (options.length === 0) return null;
        return { options, multiple: entry.flags?.isFlags === true };
    }

    export function getTypeDescriptionShort(name: string): string | null {
        const typeInfo = getNeedleTypeMeta(name);
        if (!typeInfo) return null;

        if (typeInfo.__description_text) return typeInfo.__description_text;

        const longText = typeInfo.comment?.text;
        if (longText) {
            typeInfo.__description_text = parseLongText(longText, 250);
        }
        else if (typeInfo.comment?.shortText) {
            typeInfo.__description_text = typeInfo.comment.shortText;
        }


        return typeInfo.__description_text || null;

        function parseLongText(text: string, maxLength: number): string {
            let newText = "";
            const lines = text.split("\n");
            for (const line of lines) {
                const trimmed = line.trim();
                if (trimmed.length === 0) continue;

                // if we encounter a list or section, stop parsing
                if (
                    trimmed.startsWith("-") ||
                    trimmed.startsWith("#") ||
                    trimmed.startsWith("*")
                ) break;

                newText += trimmed + " ";
                if (newText.length > maxLength) {
                    newText = newText.substring(0, maxLength - 3) + "...";
                    break;
                }
            }

            if (newText.length <= 0) {
                if (typeInfo?.comment?.shortText) {
                    return typeInfo.comment.shortText;
                }
                if (text.length > maxLength) newText = text.substring(0, maxLength - 3) + "...";
                else return text;
            }

            return newText.trim();
        }
    }
}

